import React, { useState } from "react";
import { Plus, Clock, Trash2, Check, Info } from "lucide-react";
import { INK, INK_2, INK_3, LINE, BURGUNDY, BAD_INK } from "../../Bureau/bureauTokens";
import { httpClient } from "../../../HttpClient";

const DOW_LABELS = ["Lundi", "Mardi", "Mercredi", "Jeudi", "Vendredi", "Samedi", "Dimanche"];

const fieldLabel = "text-[11px] font-medium";
const fieldInput = "h-9 px-3 text-xs outline-none";
const fieldStyle = { border: `1px solid ${LINE}`, borderRadius: 8, color: INK, background: "#fff" };

// Onglet "Créneaux récurrents" — règles d'indisponibilité hebdomadaire de
// l'utilisateur courant (voir plan). `regles`/`onChange` viennent de
// Planning.jsx (qui les réutilise aussi pour griser la grille horaire),
// pas d'un fetch local ici pour rester une seule source de vérité.
function RecurrentAvailabilityPanel({ regles, onChange }) {
  const [jour, setJour] = useState("1");
  const [heureDebut, setHeureDebut] = useState("08:00");
  const [heureFin, setHeureFin] = useState("13:00");
  const [motif, setMotif] = useState("Indisponible");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const valid = heureDebut < heureFin;

  const addRegle = async () => {
    if (!valid) return;
    setSaving(true);
    setError(null);
    try {
      await httpClient.post("/api/disponibilites", { jour: parseInt(jour, 10), heureDebut, heureFin, motif: motif.trim() || "Indisponible" });
      setMotif("Indisponible");
      await onChange();
    } catch (err) {
      setError(err.response?.data?.message || "Erreur lors de la création de la règle.");
    } finally {
      setSaving(false);
    }
  };

  const removeRegle = async (id) => {
    await httpClient.delete(`/api/disponibilites/${id}`);
    await onChange();
  };

  const sorted = [...(regles || [])].sort((a, b) => a.jour - b.jour || a.heureDebut.localeCompare(b.heureDebut));

  return (
    <>
      {error && (
        <p className="text-xs mb-2" style={{ color: BAD_INK }}>
          {error}
        </p>
      )}

      <div className="mb-3" style={{ background: "#FFF6F8", border: "1px solid #E8C8D2", borderRadius: 10, padding: "12px 14px" }}>
        <div className="flex items-center gap-1.5 text-xs font-semibold mb-2" style={{ color: BURGUNDY }}>
          <Plus size={13} /> Nouveau créneau récurrent
        </div>
        <label className={`block ${fieldLabel} mb-1`} style={{ color: INK_2 }}>
          Jour
        </label>
        <select value={jour} onChange={(e) => setJour(e.target.value)} className={`w-full ${fieldInput} mb-2.5 appearance-none cursor-pointer`} style={fieldStyle}>
          {DOW_LABELS.map((l, i) => (
            <option key={i} value={i}>
              {l}
            </option>
          ))}
        </select>
        <div className="grid grid-cols-2 gap-3 mb-2.5">
          <label className="flex flex-col gap-1">
            <span className={fieldLabel} style={{ color: INK_2 }}>
              De
            </span>
            <input type="time" value={heureDebut} onChange={(e) => setHeureDebut(e.target.value)} className={fieldInput} style={fieldStyle} />
          </label>
          <label className="flex flex-col gap-1">
            <span className={fieldLabel} style={{ color: INK_2 }}>
              À
            </span>
            <input type="time" value={heureFin} min={heureDebut} onChange={(e) => setHeureFin(e.target.value)} className={fieldInput} style={fieldStyle} />
          </label>
        </div>
        <label className={`block ${fieldLabel} mb-1`} style={{ color: INK_2 }}>
          Motif
        </label>
        <input
          value={motif}
          onChange={(e) => setMotif(e.target.value)}
          placeholder="ex. Indisponible, Réunion interne…"
          className={`w-full ${fieldInput}`}
          style={fieldStyle}
        />
        <button
          onClick={addRegle}
          disabled={!valid || saving}
          className="flex items-center gap-1.5 mt-3 px-4 py-2 text-xs font-semibold text-white disabled:opacity-50"
          style={{ background: BURGUNDY, borderRadius: 10 }}
        >
          <Check size={13} /> {saving ? "Ajout…" : "Ajouter la règle"}
        </button>
      </div>

      <div style={{ background: "#FAFAF8", border: `1px solid ${LINE}`, borderRadius: 10, padding: "12px 14px" }}>
        <div className="flex items-center gap-1.5 text-xs font-semibold mb-2" style={{ color: BURGUNDY }}>
          <Clock size={13} /> Règles actives <span style={{ color: INK_3, fontWeight: 600 }}>({sorted.length})</span>
        </div>
        {sorted.length === 0 && (
          <p className="text-xs" style={{ color: INK_3 }}>
            Aucune règle.
          </p>
        )}
        <div className="flex flex-col gap-1.5">
          {sorted.map((r) => (
            <div key={r.id} className="flex items-center gap-2 px-2.5 py-2" style={{ border: `1px solid ${LINE}`, borderRadius: 8, background: "#fff" }}>
              <span className="text-xs font-semibold" style={{ color: INK, minWidth: 72 }}>
                {DOW_LABELS[r.jour]}
              </span>
              <span className="text-[10.5px]" style={{ color: INK_3, minWidth: 90 }}>
                {r.heureDebut} – {r.heureFin}
              </span>
              <span className="text-xs flex-1 truncate" style={{ color: INK_2 }}>
                {r.motif}
              </span>
              <button
                onClick={() => removeRegle(r.id)}
                title="Supprimer"
                className="flex items-center justify-center flex-shrink-0"
                style={{ width: 26, height: 26, borderRadius: 6, color: BAD_INK }}
              >
                <Trash2 size={13} />
              </button>
            </div>
          ))}
        </div>
        <div className="flex items-center gap-1.5 mt-2.5 text-[11px]" style={{ color: INK_3 }}>
          <Info size={12} /> Ces créneaux apparaissent en zones grisées sur votre grille et sont exclus des créneaux proposés sur votre page de réservation.
        </div>
      </div>
    </>
  );
}

export default RecurrentAvailabilityPanel;
